import React from 'react'
import { FastField, Field } from 'formik'

import { Place } from '../../types/utils'
import { Customer } from '../../types/customer'
import { EndUser } from '../../types/end-user'
import placeService from '../../services/placeService'
import { graphQLClient } from '../../services/authService'
import appUserQueries from '../../services/graphql/appUsers/queries'

import FormikInput from '../FormikInput'
import FormikMultipleCheckbox from '../FormikMultipleCheckbox'
import FormikAsyncSelect from '../FormikAsyncSelect'
import customerQueries from '../../services/graphql/customers/queries'
import uniqBy from 'lodash/uniqBy'
import { useUser } from '../../hooks/useUser'
import { ExperienceFilter } from '../../types/experiences'

type ExperienceFiltersFormProps = {
  isSubmitting: boolean
}

const RATING = ['Positive', 'Negative', 'Neutral'].map((item) => ({ value: item.toLocaleUpperCase(), label: item }))
const STATUS: Array<{ label: string; value: Exclude<ExperienceFilter['status'], null> }> = [
  { label: 'Unverified', value: 'UNVERIFIED' },
  { label: 'At Location', value: 'AT_LOCATION' },
  { label: 'Verified', value: 'VERIFIED' },
]

const ExperienceFiltersForm: React.FC<ExperienceFiltersFormProps> = (props) => {
  const user = useUser()

  const placeOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    return await placeService.getAll({ where: { name_contains: inputValue } })
  }

  const areaOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const allPlace = await placeService.getAll({ where: { location: { city_contains: inputValue } } })
    return uniqBy(allPlace, 'location.city')
  }

  const customerOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const response = await graphQLClient.request(customerQueries.getAll, {
      where: { name_contains: inputValue },
    })
    return response.customers
  }

  const endUserOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const response = await graphQLClient.request(appUserQueries.getAppUsers, {
      where: { user: { displayName_contains: inputValue } },
    })
    return response.endUsers
  }

  return (
    <div className="mb-6 space-y-6 px-4 sm:px-6">
      <fieldset className="mt-8 grid grid-cols-2 space-x-6" disabled={props.isSubmitting}>
        <FastField
          type="date"
          name="date_from"
          component={FormikInput}
          label="Date From"
          placeholder="dd/mm/yy"
          max="9999-12-30"
          style={{ height: 38 }}
        />
        <FastField
          type="date"
          name="date_to"
          component={FormikInput}
          label="Date To"
          placeholder="dd/mm/yy"
          max="9999-12-30"
          style={{ height: 38 }}
        />
      </fieldset>

      <fieldset>
        <p className="mb-4 font-medium text-gray-700">Rating</p>
        <FastField name="rating" component={FormikMultipleCheckbox} items={RATING} />
      </fieldset>

      <fieldset>
        <p className="mb-4 font-medium text-gray-700">Experience</p>
        <div role="group" className="space-y-3">
          {STATUS.map((status) => (
            <div key={status.value} className="flex items-center justify-between">
              <label htmlFor={status.value} className="text-sm text-gray-700">
                {status.label}
              </label>
              <Field
                id={status.value}
                type="radio"
                name="status"
                value={status.value}
                className="form-radio h-4 w-4 border-gray-300"
              />
            </div>
          ))}
        </div>
      </fieldset>

      <Field
        name="locations"
        component={FormikAsyncSelect}
        label="Location"
        isMulti
        loadOptions={placeOptions}
        getOptionLabel={(item: Place) => item.name}
        getOptionValue={(item: Place) => item.id}
        placeholder="Type Location Name"
      />

      <Field
        name="usernames"
        component={FormikAsyncSelect}
        label="Username"
        isMulti
        loadOptions={endUserOptions}
        getOptionLabel={(item: EndUser) => item.user.displayName}
        getOptionValue={(item: EndUser) => item.id}
        placeholder="Type Username"
      />

      <Field
        name="areas"
        component={FormikAsyncSelect}
        label="Area"
        isMulti
        loadOptions={areaOptions}
        getOptionLabel={(item: Place) => item.location.city}
        getOptionValue={(item: Place) => item.location.city}
        placeholder="Type Area"
      />

      {user.data?.type === 'BUZZL' ? (
        <Field
          name="customers"
          component={FormikAsyncSelect}
          label="Customer"
          isMulti
          loadOptions={customerOptions}
          getOptionLabel={(item: Customer) => item.name}
          getOptionValue={(item: Customer) => item.id}
          placeholder="Select Customer"
        />
      ) : null}
    </div>
  )
}

export default ExperienceFiltersForm
